// AI-CONTEXT: See docs/features/subscriptions-overview-page.md
import { useState } from 'react'
import firebase from '../../firebase/compat'
import TableCell from '../common/table/TableCell.jsx'
import { MANUAL_STATUS_OPTIONS, statusLabel } from '../../helpers/subscriptionHelpers/subscriptionStatus'

/**
 * De stand van één organisatie in het abonnementenoverzicht: het label dat nu
 * geldt, met eronder het menu om hem handmatig te zetten.
 *
 * DRIE STANDEN ZIJN HANDMATIG. 'Onderdeel van pilot', 'Valt onder portfolio' en
 * 'Gestopt' staan nergens in de betaalgegevens en zijn er ook niet uit af te
 * leiden — een koepel hebben zegt niets, want veel koepelleden betalen gewoon
 * zelf. 'Betaling actief' en 'Betaling inactief' volgen uit het bedrag en de
 * periode ernaast, maar zijn ook met de hand te zetten. Alleen 'Verlopen' staat
 * niet in het menu: die volgt recht uit de einddatum ernaast.
 *
 * WAT ER NA HET OPSLAAN IN DE KOLOM KOMT, ZEGT DE SERVER. De regel "handmatig
 * wint van de betaalgegevens" staat in één bestand
 * (functions/subscriptionsOverview/resolveSubscriptionStatus.js); zou deze
 * component hem ook toepassen, dan lopen de twee uit elkaar zodra er een stand
 * bijkomt, met groene tests aan beide kanten.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * Geen opslaanknop: één keuze is één schrijfactie, geen reeks aanslagen die op
 * elkaar wachten.
 * AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
 *
 * @param {object} props
 * GESTOPT MET EEN PROJECT OP ACTIEF. Twee velden, twee vragen: deze stand zegt
 * of de klant weg is, de status in het projectenoverzicht of de omgeving
 * meetelt. Staat de tweede nog op actief, dan telt een gestopte klant mee als
 * betalende organisatie. De cel waarschuwt en zet het project niet zelf om
 * (besluit 2026-10-06) — een beheerder beslist wanneer de omgeving dichtgaat.
 * AI-CONTEXT: See docs/features/platform-revenue-churn-trend.md
 *
 * @param {{compagnyId: string, status: string, manualStatus: string, projectStatus?: string}} props.row - rij uit getSubscriptionsOverview
 * @param {(row: object) => void} props.onSaved - krijgt de bijgewerkte rij na een geslaagde opslag
 */
const SubscriptionStatusCell = ({ row, onSaved }) => {
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(/** @type {string | null} */ (null))
    // Wat de gebruiker zojuist koos, alleen zolang de opslag loopt. Zonder deze
    // tussenstand rendert React na `setSaving(true)` opnieuw met de OUDE
    // `row.manualStatus` erin, en staat het menu een halve tot twee seconden
    // uitgegrijsd op de vorige waarde — dat leest als "mijn keuze is niet
    // aangekomen", en klikken kan dan niet want het menu staat uit.
    // Bij een fout gaat hij weer op null, zodat het menu terugvalt op wat er
    // werkelijk is opgeslagen.
    const [gekozen, setGekozen] = useState(/** @type {string | null} */ (null))

    /**
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const handleChange = async (e) => {
        // Eerst uitlezen, dan pas de async stap in: `e.target` is het levende
        // DOM-element en draagt straks alweer de oude waarde.
        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
        const keuze = e.target.value

        setError(null)
        setGekozen(keuze)
        setSaving(true)
        try {
            // v2-functie in europe-west1 — region expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const setSubscriptionStatus = firebase.app().functions('europe-west1')
                .httpsCallable('setSubscriptionStatus')
            const result = await setSubscriptionStatus({
                compagnyId: row.compagnyId,
                status: keuze,
            })

            onSaved({
                ...row,
                manualStatus: result.data?.manualStatus ?? keuze,
                status: result.data?.status ?? row.status,
                // Deze twee komen alleen mee als de server de betaalgegevens
                // heeft opgezocht — dat doet hij niet bij een handmatige stand,
                // want die wint toch. Ontbreken ze, dan houdt de rij wat hij
                // had; een verzonnen `editable: true` zou bij een Stripe-klant
                // invoervelden terugzetten die de server weigert.
                paymentStatus: result.data?.paymentStatus ?? row.paymentStatus,
                editable: result.data?.editable ?? row.editable,
            })
            // De nieuwe rij komt hierboven binnen; de tussenstand mag weg,
            // anders overstemt hij voortaan de prop.
            setGekozen(null)
        } catch (err) {
            console.error('setSubscriptionStatus failed:', err)
            // De fout blijft staan én het menu springt terug naar wat er
            // werkelijk is opgeslagen: bleef het op de keuze staan, dan toont
            // het scherm iets dat de database niet weet.
            setGekozen(null)
            setError(err.message || 'Kon de stand niet opslaan.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <TableCell>
            <span className={`subscriptions-status subscriptions-status--${row.status}`}>
                {statusLabel(row.status)}
            </span>
            {/* `value` en niet `defaultValue`: React leest defaultValue alleen
                bij het aanhaken, en dan verandert het menu niet meer mee als de
                rij een andere stand krijgt.
                AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
            <select
                aria-label='Stand'
                className='subscriptions-status-select'
                value={gekozen ?? (row.manualStatus || '')}
                onChange={handleChange}
                disabled={saving}
            >
                {MANUAL_STATUS_OPTIONS.map(({ value, label }) => (
                    <option key={value || 'auto'} value={value}>{label}</option>
                ))}
            </select>
            {error && <span className='subscriptions-status-error'>{error}</span>}
            {row.status === 'stopped' && row.projectStatus === 'active' && (
                <span className='subscriptions-status-warning'>
                    Dit project staat in het projectenoverzicht nog op actief en telt mee als betalende organisatie.
                </span>
            )}
        </TableCell>
    )
}

export default SubscriptionStatusCell
